import React, { useEffect, useRef } from 'react';
import Hls from 'hls.js';
import './VideoPlayer.css';

const BASE_URL = process.env.REACT_APP_API_URL || '';

/**
 * VideoPlayer that handles two kinds of hlsUrl:
 *
 * 1. Authenticated route  — `/api/media/:id/playlist.m3u8`
 *    hls.js needs to send the JWT so it uses xhrSetup to inject
 *    the Authorization header on every XHR (playlist + segments).
 *
 * 2. Public share route   — `/api/share/view/:token/media/:id/playlist.m3u8`
 *    No auth header required; the token is in the URL path itself.
 *
 * The signed segment URLs inside the playlist already include GCS auth,
 * so the XHR header is only needed for the playlist fetch itself when
 * the route is the authenticated one.
 */
export default function VideoPlayer({ hlsUrl, thumbnailUrl, style }) {
  const videoRef = useRef(null);
  const hlsRef = useRef(null);

  useEffect(() => {
    const video = videoRef.current;
    if (!video || !hlsUrl) return;

    // Resolve relative API paths to absolute URLs
    const resolvedUrl = hlsUrl.startsWith('http')
      ? hlsUrl
      : `${BASE_URL}${hlsUrl}`;

    const isAuthRoute = hlsUrl.includes('/api/media/');

    if (Hls.isSupported()) {
      const hlsConfig = {
        maxBufferLength: 30,
        maxMaxBufferLength: 60,
        // Inject JWT on playlist + segment requests for the authenticated route.
        // For the share route the token is in the path; no header needed.
        xhrSetup: isAuthRoute
          ? (xhr) => {
              const token = localStorage.getItem('mv_token');
              if (token) xhr.setRequestHeader('Authorization', `Bearer ${token}`);
            }
          : undefined,
      };

      const hls = new Hls(hlsConfig);
      hlsRef.current = hls;
      hls.loadSource(resolvedUrl);
      hls.attachMedia(video);
      hls.on(Hls.Events.ERROR, (_, data) => {
        if (data.fatal) {
          console.error('[HLS] Fatal error:', data.type, data.details);
          hls.destroy();
        }
      });
    } else if (video.canPlayType('application/vnd.apple.mpegurl')) {
      // Safari native HLS — can't inject headers, but Safari handles
      // cookies/credentials differently; this path works for public shares.
      video.src = resolvedUrl;
    }

    return () => {
      if (hlsRef.current) {
        hlsRef.current.destroy();
        hlsRef.current = null;
      }
    };
  }, [hlsUrl]);

  return (
    <video
      ref={videoRef}
      className="video-player"
      controls
      playsInline
      poster={thumbnailUrl}
      preload="metadata"
      style={style}
    />
  );
}
